import { Button, Checkbox, EmptyState, Modal, Skeleton, UserInfo, useToast } from '@/components';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { usePollViewerCandidates, usePollViewers, useReplacePollViewers } from './hooks';

export const ViewersModal = (props: { open: boolean; pollId: string; onClose: () => void }) => {
  const viewers = usePollViewers(() => props.pollId, props.open);
  const candidates = usePollViewerCandidates(() => props.pollId, props.open);
  const save = useReplacePollViewers(props.pollId);
  const toast = useToast();
  const [selected, setSelected] = createSignal<Set<string>>(new Set());

  createEffect(() => {
    if (viewers.data) setSelected(new Set(viewers.data.data.map((user) => user.id)));
  });

  const toggle = (id: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const submit = async () => {
    await save.mutateAsync([...selected()]);
    toast.success('Dashboard viewers updated');
    props.onClose();
  };

  return (
    <Modal open={props.open} onClose={props.onClose} title="Dashboard viewers" size="md">
      <Show when={!candidates.isPending && !viewers.isPending} fallback={<Skeleton class="h-32" />}>
        <Show
          when={(candidates.data?.data.length ?? 0) > 0}
          fallback={<EmptyState title="No other users" />}
        >
          <div class="flex max-h-80 flex-col gap-3 overflow-y-auto">
            <For each={candidates.data?.data ?? []}>
              {(user) => (
                <Checkbox
                  checked={selected().has(user.id)}
                  onChange={(checked) => toggle(user.id, checked)}
                  label={<UserInfo user={user} />}
                />
              )}
            </For>
          </div>
        </Show>
      </Show>
      <div class="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button loading={save.isPending} onClick={submit}>
          Save viewers
        </Button>
      </div>
    </Modal>
  );
};
